import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add as AddIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import apiService from '../services/api';

interface TimelineManagementProps {
  open: boolean;
  onClose: () => void;
  outage: any | null;
  outageDetails: any | null;
  outageType?: 'voice' | 'webservices'; // Determines which API methods to use
}

const ENTRY_TYPES = ['update', 'status_change', 'note', 'resolved'];

const TimelineManagement: React.FC<TimelineManagementProps> = ({
  open,
  onClose,
  outage,
  outageDetails,
  outageType = 'voice', // Default to voice for backward compatibility
}) => {
  const [timeline, setTimeline] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({
    description: '',
    entry_type: 'update',
    timestamp: new Date(),
    status: outage?.status || '',
  });

  useEffect(() => {
    if (open && outageDetails?.timeline) {
      setTimeline(outageDetails.timeline);
    } else if (open && outage) {
      loadTimeline();
    }
  }, [open, outage, outageDetails, outageType]);

  const loadTimeline = async () => {
    if (!outage) return;
    
    try {
      setLoading(true);
      const timelineData = outageType === 'webservices' 
        ? await apiService.getWebServicesOutageTimeline(outage.id)
        : await apiService.getVoiceOutageTimeline(outage.id);
      setTimeline(timelineData);
    } catch (err: any) {
      setError(`Failed to load timeline: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    if (!outage) return;
    
    if (!newEntry.description.trim()) {
      setError('Description is required');
      return;
    }

    try {
      setAddingEntry(true);
      setError(null);
      
      if (outageType === 'webservices') {
        await apiService.addWebServicesOutageTimelineEntry(outage.id, {
          description: newEntry.description.trim(),
          entry_type: newEntry.entry_type,
          timestamp: newEntry.timestamp.toISOString(),
          status: newEntry.status || undefined,
        });
      } else {
        await apiService.addVoiceOutageTimelineEntry(outage.id, {
          description: newEntry.description.trim(),
          entry_type: newEntry.entry_type,
          timestamp: newEntry.timestamp.toISOString(),
          status: newEntry.status || undefined,
        });
      }

      // Reset form
      setNewEntry({
        description: '',
        entry_type: 'update',
        timestamp: new Date(),
        status: outage?.status || '',
      });

      // Reload timeline
      await loadTimeline();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add timeline entry');
    } finally {
      setAddingEntry(false);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const aestDate = utcToZonedTime(date, 'Australia/Sydney');
      return format(aestDate, 'dd/MM/yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  const getEntryTypeColor = (entryType: string) => {
    switch (entryType) {
      case 'resolved':
        return 'success';
      case 'status_change':
        return 'info';
      case 'created':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Timeline - {outage?.incident_id || 'Voice Outage'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Add New Entry Form */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Add Timeline Entry
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={newEntry.description}
                  onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                  required
                  placeholder="Enter a description for this timeline entry..."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Entry Type"
                  value={newEntry.entry_type}
                  onChange={(e) => setNewEntry({ ...newEntry, entry_type: e.target.value })}
                  SelectProps={{ native: false }}
                >
                  {ENTRY_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <DateTimePicker
                  label="Timestamp"
                  value={newEntry.timestamp}
                  onChange={(newValue) => setNewEntry({ ...newEntry, timestamp: newValue || new Date() })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              {newEntry.entry_type === 'status_change' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="New Status"
                    value={newEntry.status}
                    onChange={(e) => setNewEntry({ ...newEntry, status: e.target.value })}
                    SelectProps={{ native: false }}
                  >
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddEntry}
                  disabled={addingEntry || !newEntry.description.trim()}
                >
                  {addingEntry ? <CircularProgress size={20} /> : 'Add Entry'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Timeline List */}
          <Typography variant="h6" gutterBottom>
            Timeline Entries ({timeline.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : timeline.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No timeline entries yet
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {timeline.map((entry, index) => (
                <Card key={index} variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box sx={{ flex: 1 }}>
                        <Box display="flex" gap={1} alignItems="center" mb={1}>
                          <Chip
                            label={entry.entry_type}
                            color={getEntryTypeColor(entry.entry_type) as any}
                            size="small"
                          />
                          {entry.status && (
                            <Chip
                              label={`Status: ${entry.status}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {entry.created_by_username ? `by ${entry.created_by_username}` : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {formatDateTime(entry.timestamp)}
                        </Typography>
                        <Typography variant="body1">
                          {entry.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TimelineManagement;

